import React from 'react';
import { Switch } from '@heroui/react';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
} from 'recharts';

const formatHour = (h) => {
    if (h === 0 || h === 24) return '12 AM';
    if (h === 12) return '12 PM';
    return h > 12 ? `${h - 12} PM` : `${h} AM`;
};

const formatTimeRange = (h) => {
    return `${formatHour(h)} to ${formatHour(h + 1)}`;
};

const ConvertedTooltip = ({ active, payload, label, pickerType, pacingType }) => {
    if (!active || !payload || !payload.length) {
        return null;
    }

    const data = payload[0].payload;

    const tooltipTitle = pickerType === 'date'
        ? formatTimeRange(data.originalIndex)
        : label;

    const convertedValue =
        pacingType === 'cumulative'
            ? data.cumulativeConverted
            : data.uniqueConvertedLeads;

    const convertedPercentage =
        pacingType === 'cumulative'
            ? data.cumConvertedPct
            : data.convertedPct;

    return (
        <div className="bg-slate-800/95 backdrop-blur-md border border-slate-700/50 rounded-xl p-4 shadow-2xl text-white text-xs w-56 pointer-events-none select-none z-50">
            <div className="font-bold border-b border-slate-600/50 pb-2 mb-2 text-slate-300">
                {tooltipTitle}
            </div>

            <div className="text-[9px] text-slate-400 font-semibold uppercase tracking-wider mb-2">
                {pacingType === 'cumulative'
                    ? 'Cumulative Pacing'
                    : 'Period Pacing'}
            </div>

            <div className="flex justify-between items-center">
                <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-violet-500"></span>
                    <span className="text-slate-400">Converted</span>
                </div>

                <div className="flex gap-1 items-end">
                    <b className="text-white font-semibold">
                        {convertedValue}
                    </b>

                    <span className="text-[10px] text-violet-400">
                        ({convertedPercentage}%)
                    </span>
                </div>
            </div>
        </div>
    );
};

const ConvertedChart = ({
    data = [],
    pickerType = 'date',
    pacingType = 'current',
    useConversionDateRange,
    setUseConversionDateRange,
    ExecuteLineChartCounts,
    selectedLineDate
}) => {
    const activeConvertedKey =
        pacingType === 'cumulative'
            ? 'cumulativeConverted'
            : 'uniqueConvertedLeads';

    const convertedMax = Math.max(
        ...data.map(item => Number(item[activeConvertedKey]) || 0),
        0
    );

    const getConvertedMax = (max) => {
        if (max <= 0) return 10;

        if (max <= 10) {
            return Math.ceil(max / 2) * 2;
        }

        if (max <= 50) {
            return Math.ceil(max / 5) * 5;
        }

        if (max <= 100) {
            return Math.ceil(max / 10) * 10;
        }

        if (max <= 500) {
            return Math.ceil(max / 50) * 50;
        }

        if (max <= 1000) {
            return Math.ceil(max / 100) * 100;
        }

        if (max <= 5000) {
            return Math.ceil(max / 500) * 500;
        }

        return Math.ceil(max * 1.1);
    };

    const maxVal = getConvertedMax(convertedMax);

    return (
        <div className="w-full bg-white rounded-2xl p-6 border border-slate-100 shadow-medium mt-4">

            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-slate-800">
                        Converted Leads
                    </h2>

                    <p className="text-slate-500 text-sm">
                        Converted leads performance
                    </p>
                </div>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-3 h-9 bg-white shadow-sm">
                        <span className="text-[11px] font-semibold text-slate-500 whitespace-nowrap">Converted Date Range</span>
                        <Switch
                            size="sm"
                            isSelected={useConversionDateRange === 1}
                            onValueChange={(isSelected) => {
                                const newVal = isSelected ? 1 : 0;
                                setUseConversionDateRange(newVal);
                                if (ExecuteLineChartCounts) {
                                    ExecuteLineChartCounts(selectedLineDate, newVal);
                                }
                            }}
                        />
                    </div>
                    <div className="flex items-center gap-2 text-xs font-semibold">
                        <span className="w-3 h-3 rounded-full bg-violet-500"></span>
                        <span>Converted</span>
                    </div>
                </div>
            </div>

            <div className="w-full h-80 mt-2 z-10 relative">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={data}
                        margin={{
                            top: 10,
                            right: 10,
                            left: 0,
                            bottom: 0,
                        }}
                    >
                        <defs>
                            <linearGradient
                                id="colorConvertedOnly"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="5%"
                                    stopColor="#8b5cf6"
                                    stopOpacity={0.15}
                                />

                                <stop
                                    offset="95%"
                                    stopColor="#8b5cf6"
                                    stopOpacity={0.01}
                                />
                            </linearGradient>
                        </defs>

                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#f1f5f9"
                        />

                        <XAxis
                            dataKey="label"
                            tickLine={false}
                            axisLine={false}
                            tick={{
                                fill: '#94a3b8',
                                fontSize: 10,
                                fontWeight: 500,
                            }}
                            dy={10}
                        />

                        <YAxis
                            tickLine={false}
                            axisLine={false}
                            tick={{
                                fill: '#94a3b8',
                                fontSize: 10,
                                fontWeight: 500,
                            }}
                            dx={-5}
                            domain={[0, maxVal]}
                        />

                        <RechartsTooltip
                            content={
                                <ConvertedTooltip
                                    pickerType={pickerType}
                                    pacingType={pacingType}
                                />
                            }
                            cursor={{
                                stroke: '#cbd5e1',
                                strokeWidth: 1,
                                strokeDasharray: '4 4',
                            }}
                            wrapperStyle={{
                                zIndex: 100,
                            }}
                        />

                        <Area
                            type="monotone"
                            dataKey={activeConvertedKey}
                            stroke="#8b5cf6"
                            strokeWidth={2.5}
                            fillOpacity={1}
                            fill="url(#colorConvertedOnly)"
                            activeDot={{
                                r: 6,
                                strokeWidth: 0,
                            }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default ConvertedChart;